import React, { useMemo, useState } from "react"

import useOnclickOutside from "react-cool-onclickoutside"

import { IOneVerifyPracticeRole } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"

import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { userDetailsSetSelectedPracticeRole } from "../../store/slices/userDetails.slice"

import checkIcon from "@front-shared/assets/images/icons/check-dark-no-outline.svg"
import chevronDown from "@front-shared/assets/images/icons/chevron-down-dark-small.svg"

export const SelectedPracticeComponent: React.FunctionComponent<{
    className?: string
}> = props => {
    const dispatch = useAppDispatch()

    const showPracticeSelection = () => {
        setPracticeSelectActive(true)
    }

    const hidePracticeSelection = () => {
        setPracticeSelectActive(false)
    }

    const containerRef = useOnclickOutside(hidePracticeSelection)

    const profile = useAppSelector(profileSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [practiceSelectActive, setPracticeSelectActive] =
        useState<boolean>(false)

    const practiceSelectionAvailable = useMemo<boolean>(
        () => !!profile && profile.practiceRoles.length > 1,
        [profile?.practiceRoles],
    )

    const isPracticeRoleSelected = (
        practiceRole: IOneVerifyPracticeRole,
    ): boolean => practiceRole.id === selectedPracticeRole?.id

    const onPracticeSelected = (practiceRole: IOneVerifyPracticeRole) => {
        if (!isPracticeRoleSelected(practiceRole)) {
            dispatch(userDetailsSetSelectedPracticeRole(practiceRole))
        }

        hidePracticeSelection()
    }

    return profile && selectedPracticeRole ? (
        <div
            ref={containerRef}
            className={`
                relative rounded-full flex items-center gap-[8px] px-[16px] py-[10px]
                ${props.className}
                ${
                    practiceSelectionAvailable
                        ? "cursor-pointer border border-solid border-brand-primary"
                        : "disabled-button"
                }
            `}
            onClick={
                practiceSelectionAvailable && !practiceSelectActive
                    ? showPracticeSelection
                    : undefined
            }
        >
            <PrimaryText typography="buttonText" className="pt-[3px]">
                {selectedPracticeRole.practice.name}
            </PrimaryText>

            {practiceSelectionAvailable && (
                <img src={chevronDown} alt="Toggle" className="ml-[6px]" />
            )}

            {/*practice selection if multiple*/}
            {practiceSelectActive && (
                <div
                    className="
                        absolute w-[300px] z-100 bg-brand-offWhite top-[50px] right-0 sm:right-[-50px]
                        rounded-[8px] border border-solid border-brand-warmShadow
                        overflow-hidden
                    "
                >
                    {profile.practiceRoles.map((item, index) => (
                        <div
                            className={`
                                flex items-center p-[16px]
                                ${
                                    profile?.practiceRoles[index + 1]
                                        ? "border-b border-solid border-brand-warmShadow"
                                        : ""
                                }
                                ${
                                    isPracticeRoleSelected(item)
                                        ? "cursor-default bg-brand-lilacLight"
                                        : "cursor-pointer bg-brand-white"
                                }
                            `}
                            onClick={() => {
                                onPracticeSelected(item)
                            }}
                            key={index}
                        >
                            <PrimaryText
                                className="flex-1 mr-[16px]"
                                typography="textSemibold"
                            >
                                {item.practice.name}
                            </PrimaryText>

                            {isPracticeRoleSelected(item) ? (
                                <img src={checkIcon} alt="Selected" />
                            ) : (
                                <div className="w-[12px]" />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    ) : null
}
